import React from "react"
import toast from "react-hot-toast"
import { useApp } from "../context/AppContext"
import { Icon, useT, useIsMobile } from "../shared"
import { useOrgName } from "../shared/BrandLogo"
import { Card, Btn, Badge, Avatar, Segmented, Tabs, TableWrap } from "../shared/components"
import {
  listSessions,
  createSession,
  updateSession,
  deleteSession,
  endSession,
  getRsvp,
  setRsvp,
} from "../api/sessions.api"
import { getZoomStatus } from "../api/settings.api"
import { sendSessionReminder } from "../api/emails.api"
import { listProgramTrainers } from "../api/programs.api"
import { durasiDariJam, jamSelesaiDari, labelDurasi } from "../lib/sessionTime"

function toLocalInputValue(date) {
  if (!date) return ""
  const d = new Date(date)
  const pad = (n) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function fmtSchedule(date, lang) {
  if (!date) return "—"
  const d = new Date(date)
  return d.toLocaleString(lang === "id" ? "id-ID" : "en-US", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function sessionTypeLabel(type, lang) {
  if (type === "LECTURE") return lang === "id" ? "Kuliah" : "Lecture"
  if (type === "DRILL") return "Drill"
  if (type === "EXAM") return lang === "id" ? "Ujian" : "Exam"
  return type || null
}

function sessionTypeTone(type) {
  if (type === "EXAM") return "danger"
  if (type === "DRILL") return "warning"
  return "default"
}

// Session card — shared by both participant and trainer/admin views
function SessionCard({ s, lang, navigate, canEdit, rsvpStatus, onRsvp, onEdit, onDelete, onEndSession }) {
  const d = new Date(s.scheduled_at)
  const month = d.toLocaleString(lang === "id" ? "id-ID" : "en-US", { month: "short" })
  const day = d.getDate()
  const timeStr = d.toLocaleTimeString(lang === "id" ? "id-ID" : "en-US", { hour: "2-digit", minute: "2-digit" })
  const endStr = s.duration_min
    ? new Date(d.getTime() + s.duration_min * 60000).toLocaleTimeString(
        lang === "id" ? "id-ID" : "en-US",
        { hour: "2-digit", minute: "2-digit" }
      )
    : null

  const now = new Date()
  const diffMs = d - now
  const soon = diffMs > 0 && diffMs < 3 * 24 * 60 * 60 * 1000
  const isPast = d < now

  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="row gap-3" style={{ alignItems: "flex-start" }}>
        {/* Date chip */}
        <div
          style={{
            width: 64,
            padding: "8px 0",
            textAlign: "center",
            background: soon ? "var(--program-color)" : "var(--surface-2)",
            color: soon ? "white" : "var(--text)",
            borderRadius: 8,
            flexShrink: 0,
            border: "1px solid var(--border-subtle)",
          }}
        >
          <div
            style={{
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              opacity: 0.7,
            }}
          >
            {month}
          </div>
          <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1, marginTop: 2 }}>{day}</div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="row gap-2" style={{ flexWrap: "wrap" }}>
            {s.session_type && (
              <Badge tone={sessionTypeTone(s.session_type)}>
                {sessionTypeLabel(s.session_type, lang)}
              </Badge>
            )}
            {soon && (
              <Badge tone="brand" dot>
                {lang === "id" ? "Dalam 2 hari" : "In 2 days"}
              </Badge>
            )}
            {s.is_active && (
              <Badge tone="success" dot>
                {lang === "id" ? "Aktif" : "Live"}
              </Badge>
            )}
            {isPast && rsvpStatus === "attended" && (
              <Badge tone="success" dot>
                {lang === "id" ? "Hadir" : "Attended"}
              </Badge>
            )}
          </div>
          <div
            className="h3"
            style={{ marginTop: 6, cursor: "pointer" }}
            onClick={() => navigate("session-detail", { sessionId: s.id })}
          >
            {s.title}
          </div>
          <div className="row gap-4 mt-2 muted" style={{ fontSize: 12, flexWrap: "wrap" }}>
            <span className="row gap-1">
              <Icon name="clock" size={12} />
              {timeStr}{endStr ? ` – ${endStr}` : ""}
            </span>
            {s.location && (
              <span className="row gap-1">
                <Icon name="mapPin" size={12} />
                {s.location}
              </span>
            )}
            {canEdit && (
              <span className="row gap-1">
                <Icon name="users" size={12} />
                {s._count?.attendances ?? 0}
                {s.capacity ? ` / ${s.capacity} RSVPs` : ` ${lang === "id" ? "hadir" : "attended"}`}
              </span>
            )}
            {s.trainer?.full_name && (
              <span className="row gap-1">
                <Icon name="user" size={12} />
                {s.trainer.full_name}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        {canEdit ? (
          <div className="row gap-2" style={{ flexShrink: 0 }}>
            <Btn
              variant="ghost"
              size="sm"
              icon={<Icon name="eye" size={12} />}
              onClick={() => navigate("session-detail", { sessionId: s.id })}
            >
              Detail
            </Btn>
            {s.is_active ? (
              <Btn
                variant="primary"
                size="sm"
                icon={<Icon name="qrCode" size={12} />}
                onClick={() => navigate("qr", { sessionId: s.id })}
              >
                {lang === "id" ? "QR Aktif" : "Live QR"}
              </Btn>
            ) : (
              <Btn
                size="sm"
                icon={<Icon name="qrCode" size={12} />}
                onClick={() => navigate("qr", { sessionId: s.id })}
              >
                QR Panel
              </Btn>
            )}
            {s.is_active && (
              <Btn variant="ghost" size="sm" onClick={() => onEndSession(s.id)}>
                {lang === "id" ? "Akhiri" : "End"}
              </Btn>
            )}
            <Btn variant="ghost" size="sm" icon={<Icon name="edit" size={12} />} onClick={() => onEdit(s)} />
            <Btn variant="ghost" size="sm" icon={<Icon name="trash" size={12} />} onClick={() => onDelete(s)} />
          </div>
        ) : (
          <div className="col gap-2" style={{ alignItems: "flex-end", flexShrink: 0 }}>
            {rsvpStatus === "GOING" && (
              <Badge tone="success" dot>
                {lang === "id" ? "Akan hadir" : "Going"}
              </Badge>
            )}
            {rsvpStatus === "NOT_GOING" && (
              <Badge tone="default">{lang === "id" ? "Tidak hadir" : "Not going"}</Badge>
            )}
            {!rsvpStatus && !isPast && (
              <div className="row gap-1">
                <Btn size="sm" onClick={() => onRsvp(s.id, "NOT_GOING")}>
                  {lang === "id" ? "Tidak hadir" : "Not going"}
                </Btn>
                <Btn size="sm" variant="primary" onClick={() => onRsvp(s.id, "GOING")}>
                  RSVP
                </Btn>
              </div>
            )}
            {soon && (
              <Btn
                size="sm"
                variant="primary"
                icon={<Icon name="qrCode" size={12} />}
                onClick={() => navigate("qr")}
              >
                {lang === "id" ? "Check in" : "Check in"}
              </Btn>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default function SessionsScreen({ embedded = false }) {
  const t = useT()
  const { lang, navigate, currentProgramId, currentProgram, currentClassId, currentClass, role } = useApp()
  const orgName = useOrgName()
  const isMobile = useIsMobile()
  const canEdit = role === "trainer" || role === "admin"
  const [sessions, setSessions] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [showForm, setShowForm] = React.useState(false)
  const [editing, setEditing] = React.useState(null)
  const [view, setView] = React.useState("list")
  const [tab, setTab] = React.useState("upcoming")
  const [rsvpMap, setRsvpMap] = React.useState({}) // { [sessionId]: 'GOING' | 'NOT_GOING' }

  const reload = React.useCallback(() => {
    if (!currentProgramId) { setSessions([]); return }
    setLoading(true)
    listSessions(currentProgramId, currentClassId)
      .then((data) => {
        setSessions(data)
        return data
      })
      .catch((e) => toast.error(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false))
  }, [currentProgramId, currentClassId])

  React.useEffect(() => { reload() }, [reload])

  // Load RSVP statuses for participant
  React.useEffect(() => {
    if (canEdit || sessions.length === 0) return
    const upcoming = sessions.filter((s) => new Date(s.scheduled_at) >= new Date())
    Promise.allSettled(upcoming.map((s) => getRsvp(s.id).then((r) => ({ id: s.id, status: r.myStatus }))))
      .then((results) => {
        const map = {}
        for (const r of results) {
          if (r.status === "fulfilled" && r.value?.status) {
            map[r.value.id] = r.value.status
          }
        }
        setRsvpMap(map)
      })
  }, [sessions, canEdit])

  const handleDelete = async (s) => {
    if (!window.confirm(lang === "id" ? "Hapus sesi ini?" : "Delete this session?")) return
    let series = false
    if (s.series_id) {
      series = window.confirm(
        lang === "id"
          ? "Sesi ini bagian dari rangkaian berulang.\nOK = hapus SELURUH rangkaian · Batal = hapus sesi ini saja."
          : "This session is part of a recurring series.\nOK = delete the WHOLE series · Cancel = delete only this one."
      )
    }
    try {
      await deleteSession(s.id, { series })
      toast.success(lang === "id" ? "Sesi dihapus" : "Session deleted")
      reload()
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    }
  }

  const handleSendReminder = async (id) => {
    if (!window.confirm(lang === "id" ? "Kirim email pengingat ke semua peserta program?" : "Send reminder email to all program participants?")) return
    try {
      const result = await sendSessionReminder(currentProgramId, id)
      toast.success(
        lang === "id"
          ? `Pengingat dikirim: ${result.sent} berhasil, ${result.failed} gagal`
          : `Reminder sent: ${result.sent} ok, ${result.failed} failed`
      )
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    }
  }

  const handleEndSession = async (id) => {
    try {
      await endSession(id)
      toast.success(lang === "id" ? "Sesi diakhiri" : "Session ended")
      reload()
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    }
  }

  const handleRsvp = async (sessionId, status) => {
    try {
      await setRsvp(sessionId, status)
      setRsvpMap((prev) => ({ ...prev, [sessionId]: status }))
      toast.success(
        status === "GOING"
          ? (lang === "id" ? "RSVP: Anda terdaftar hadir" : "RSVP: You're going")
          : (lang === "id" ? "RSVP: Anda terdaftar tidak hadir" : "RSVP: You're not going")
      )
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    }
  }

  const openCreate = () => { setEditing(null); setShowForm(true) }
  const openEdit = (s) => { setEditing(s); setShowForm(true) }

  const now = new Date()
  const upcoming = sessions.filter((s) => new Date(s.scheduled_at) >= now)
  const past = sessions.filter((s) => new Date(s.scheduled_at) < now)
  const awaitingRsvp = upcoming.filter((s) => !rsvpMap[s.id])

  const activeSession = sessions.find((s) => s.is_active)

  // Tab display for participant
  const tabSessions = tab === "past" ? past : tab === "rsvp" ? awaitingRsvp : upcoming

  const cardProps = {
    lang,
    navigate,
    canEdit,
    rsvpMap,
    onRsvp: handleRsvp,
    onEdit: openEdit,
    onDelete: handleDelete,
    onEndSession: handleEndSession,
  }

  const summaryText =
    upcoming.length > 0
      ? `${upcoming.length} ${lang === "id" ? "sesi mendatang" : "upcoming sessions"}${past.length > 0 ? ` · ${past.length} ${lang === "id" ? "lampau" : "past"}` : ""}`
      : lang === "id"
      ? "Belum ada sesi terjadwal"
      : "No sessions scheduled yet"

  const headerActions = (
    <div className="page-header-actions">
      <Segmented
        options={[
          { value: "list", label: lang === "id" ? "Daftar" : "List" },
          { value: "calendar", label: lang === "id" ? "Kalender" : "Calendar" },
        ]}
        value={view}
        onChange={setView}
      />
      {canEdit && (
        <Btn
          variant="primary"
          icon={<Icon name="plus" size={14} />}
          onClick={openCreate}
          disabled={!currentProgramId}
        >
          {lang === "id" ? "Buat Sesi" : "New session"}
        </Btn>
      )}
    </div>
  )

  return (
    <div className={embedded ? "" : "main-inner"}>
      {/* Page Header — full header standalone; compact toolbar when embedded
          in the unified Training module (which owns the title). */}
      {embedded ? (
        <div
          className="row between"
          style={{ alignItems: "flex-end", flexWrap: "wrap", gap: 12, marginBottom: 16 }}
        >
          <div className="muted small">{summaryText}</div>
          {headerActions}
        </div>
      ) : (
        <div className="page-header">
          <div className="page-header-text">
            <div className="eyebrow">
              {currentProgram?.code?.toUpperCase() || orgName} · {lang === "id" ? "Sesi" : "Sessions"}
            </div>
            <h1 className="h1" style={{ marginTop: 6 }}>
              {lang === "id" ? "Sesi Pelatihan" : "Training sessions"}
            </h1>
            <div className="muted mt-1">{summaryText}</div>
          </div>
          {headerActions}
        </div>
      )}

      {loading ? (
        <div className="muted mt-5">{lang === "id" ? "Memuat..." : "Loading..."}</div>
      ) : view === "calendar" ? (
        <SessionCalendar sessions={sessions} lang={lang} navigate={navigate} />
      ) : canEdit ? (
        /* ── Trainer / Admin view ── */
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 320px", gap: 16 }}>
          {/* Session cards */}
          <div className="col gap-3">
            {sessions.length === 0 ? (
              <div className="card" style={{ padding: 32, textAlign: "center" }}>
                <div className="muted">
                  {lang === "id" ? "Belum ada sesi untuk program ini." : "No sessions for this program yet."}
                </div>
                <Btn variant="primary" style={{ marginTop: 12 }} onClick={openCreate}>
                  {lang === "id" ? "Buat sesi pertama" : "Create first session"}
                </Btn>
              </div>
            ) : (
              <>
                {upcoming.length > 0 && (
                  <>
                    <div className="eyebrow" style={{ marginTop: 8 }}>
                      {lang === "id" ? "Sesi mendatang" : "Upcoming"} · {upcoming.length}
                    </div>
                    {upcoming.map((s) => (
                      <SessionCard key={s.id} s={s} {...cardProps} />
                    ))}
                  </>
                )}
                {past.length > 0 && (
                  <>
                    <div className="eyebrow" style={{ marginTop: 16, opacity: 0.6 }}>
                      {lang === "id" ? "Sesi lampau" : "Past sessions"} · {past.length}
                    </div>
                    {past.map((s) => (
                      <div key={s.id} style={{ opacity: 0.65 }}>
                        <SessionCard s={s} {...cardProps} />
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="col gap-4">
            {activeSession ? (
              <Card title={lang === "id" ? "Sesi QR Aktif" : "Live QR session"} eyebrow={activeSession.title}>
                <div className="col gap-2 mt-2">
                  <Btn
                    variant="primary"
                    block
                    icon={<Icon name="qrCode" size={14} />}
                    onClick={() => navigate("qr", { sessionId: activeSession.id })}
                  >
                    {lang === "id" ? "Buka panel QR" : "Open QR panel"}
                  </Btn>
                  <Btn
                    block
                    onClick={() => handleEndSession(activeSession.id)}
                  >
                    {lang === "id" ? "Akhiri sesi" : "End session"}
                  </Btn>
                </div>
                <div className="col gap-2 mt-3">
                  <div className="row between" style={{ fontSize: 12 }}>
                    <span className="muted">{lang === "id" ? "Hadir" : "Attended"}</span>
                    <span className="mono">{activeSession._count?.attendances ?? 0}</span>
                  </div>
                  {activeSession.capacity && (
                    <div className="row between" style={{ fontSize: 12 }}>
                      <span className="muted">{lang === "id" ? "Kapasitas" : "Capacity"}</span>
                      <span className="mono">{activeSession.capacity}</span>
                    </div>
                  )}
                </div>
              </Card>
            ) : (
              <Card title={lang === "id" ? "Sesi QR" : "QR session"} eyebrow={lang === "id" ? "Tidak ada sesi aktif" : "No active session"}>
                <div className="muted" style={{ fontSize: 12 }}>
                  {lang === "id"
                    ? "Klik tombol QR pada sesi untuk memulai absensi."
                    : "Click a session's QR button to start attendance."}
                </div>
              </Card>
            )}

            <Card
              title={lang === "id" ? "Ringkasan" : "Summary"}
              eyebrow={currentProgram?.name || "Program"}
            >
              <div className="col gap-2">
                <div className="row between" style={{ fontSize: 12 }}>
                  <span className="muted">{lang === "id" ? "Mendatang" : "Upcoming"}</span>
                  <span className="mono">{upcoming.length}</span>
                </div>
                <div className="row between" style={{ fontSize: 12 }}>
                  <span className="muted">{lang === "id" ? "Sudah berlalu" : "Past"}</span>
                  <span className="mono">{past.length}</span>
                </div>
                <div className="row between" style={{ fontSize: 12 }}>
                  <span className="muted">{lang === "id" ? "Total absensi" : "Total attendance"}</span>
                  <span className="mono">
                    {past.reduce((acc, s) => acc + (s._count?.attendances ?? 0), 0)}
                  </span>
                </div>
              </div>
            </Card>

            {canEdit && upcoming.length > 0 && (
              <Card title={lang === "id" ? "Kirim pengingat" : "Send reminder"} eyebrow={lang === "id" ? "Email peserta" : "Email participants"}>
                <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
                  {lang === "id"
                    ? "Kirim email pengingat ke semua peserta program untuk sesi mendatang."
                    : "Send a reminder email to all program participants for upcoming sessions."}
                </div>
                {upcoming.slice(0, 3).map((s) => {
                  const d = new Date(s.scheduled_at)
                  return (
                    <div
                      key={s.id}
                      className="row between"
                      style={{
                        padding: "6px 0",
                        borderBottom: "1px solid var(--border-subtle)",
                        fontSize: 12,
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {s.title}
                      </span>
                      <Btn
                        variant="ghost"
                        size="sm"
                        icon={<Icon name="send" size={11} />}
                        onClick={() => handleSendReminder(s.id)}
                        style={{ flexShrink: 0, marginLeft: 6 }}
                      />
                    </div>
                  )
                })}
              </Card>
            )}
          </div>
        </div>
      ) : (
        /* ── Participant view ── */
        <div>
          <Tabs
            tabs={[
              { id: "upcoming", label: lang === "id" ? "Mendatang" : "Upcoming", count: upcoming.length },
              { id: "past", label: lang === "id" ? "Lampau" : "Past", count: past.length },
              { id: "rsvp", label: lang === "id" ? "Perlu RSVP" : "Awaiting RSVP", count: awaitingRsvp.length },
            ]}
            value={tab}
            onChange={setTab}
          />

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 320px", gap: 16, marginTop: 20 }}>
            <div className="col gap-3">
              {tabSessions.length === 0 ? (
                <div className="card" style={{ padding: 32, textAlign: "center" }}>
                  <div className="muted">
                    {tab === "upcoming"
                      ? (lang === "id" ? "Tidak ada sesi mendatang." : "No upcoming sessions.")
                      : tab === "past"
                      ? (lang === "id" ? "Belum ada sesi yang sudah berlalu." : "No past sessions yet.")
                      : (lang === "id" ? "Semua sesi sudah di-RSVP." : "All sessions RSVPd.")}
                  </div>
                </div>
              ) : (
                tabSessions.map((s) => (
                  <SessionCard key={s.id} s={s} {...cardProps} rsvpStatus={rsvpMap[s.id]} />
                ))
              )}
            </div>

            {/* Attendance sidebar */}
            <Card
              title={lang === "id" ? "Kehadiran" : "Attendance"}
              eyebrow={lang === "id" ? "Batas kelulusan ≥ 80%" : "Eligibility threshold ≥ 80%"}
            >
              {past.length > 0 ? (
                <>
                  <div className="display" style={{ fontSize: 48, lineHeight: 1, marginTop: 4 }}>
                    {Math.round((past.reduce((a, s) => a + (s._count?.attendances > 0 ? 1 : 0), 0) / Math.max(past.length, 1)) * 100)}
                    <span style={{ fontSize: 18, color: "var(--text-3)" }}>%</span>
                  </div>
                  <div className="muted mt-2" style={{ fontSize: 12 }}>
                    {lang === "id"
                      ? `${past.length} sesi sudah berlalu`
                      : `${past.length} past sessions`}
                  </div>
                  <div className="divider mt-3" />
                </>
              ) : (
                <div className="muted" style={{ fontSize: 12 }}>
                  {lang === "id" ? "Belum ada sesi yang selesai." : "No completed sessions yet."}
                </div>
              )}
              <div className="col gap-2 mt-3">
                <div className="row between" style={{ fontSize: 12 }}>
                  <span className="muted">{lang === "id" ? "Mendatang" : "Upcoming"}</span>
                  <span className="mono">{upcoming.length}</span>
                </div>
                <div className="row between" style={{ fontSize: 12 }}>
                  <span className="muted">{lang === "id" ? "Sudah berlalu" : "Past"}</span>
                  <span className="mono">{past.length}</span>
                </div>
                {Object.values(rsvpMap).filter((v) => v === "GOING").length > 0 && (
                  <div className="row between" style={{ fontSize: 12 }}>
                    <span className="muted">RSVP Going</span>
                    <span className="mono" style={{ color: "var(--success)" }}>
                      {Object.values(rsvpMap).filter((v) => v === "GOING").length}
                    </span>
                  </div>
                )}
              </div>
              {upcoming.length > 0 && (
                <div
                  style={{
                    padding: 12,
                    background: "var(--program-color-soft)",
                    borderRadius: 8,
                    fontSize: 12,
                    color: "var(--program-color-text)",
                    marginTop: 14,
                  }}
                >
                  {lang === "id"
                    ? "Hadiri minimal 80% sesi untuk memenuhi syarat sertifikat."
                    : "Attend at least 80% of sessions to remain certificate-eligible."}
                </div>
              )}
            </Card>
          </div>
        </div>
      )}

      {showForm && (
        <SessionFormModal
          programId={currentProgramId}
          classId={currentClassId}
          session={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); reload() }}
        />
      )}
    </div>
  )
}

function SessionCalendar({ sessions, lang, navigate }) {
  const [month, setMonth] = React.useState(() => {
    const d = new Date()
    return new Date(d.getFullYear(), d.getMonth(), 1)
  })
  const year = month.getFullYear()
  const mon = month.getMonth()
  const monthLabel = month.toLocaleString(lang === "id" ? "id-ID" : "en-US", {
    month: "long", year: "numeric",
  })

  const first = new Date(year, mon, 1)
  const days = []
  for (let i = 0; i < 42; i++) {
    days.push(new Date(year, mon, 1 - first.getDay() + i))
  }

  const byDay = {}
  for (const s of sessions) {
    const d = new Date(s.scheduled_at)
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
    ;(byDay[key] = byDay[key] || []).push(s)
  }

  const dayNames = lang === "id"
    ? ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"]
    : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
  const now = new Date()
  const todayKey = `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`

  const isMobile = useIsMobile()
  const monthSessions = sessions
    .filter((s) => {
      const d = new Date(s.scheduled_at)
      return d.getFullYear() === year && d.getMonth() === mon
    })
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))

  return (
    <div className="card mt-5">
      <div className="card-header">
        <div className="h3">{monthLabel}</div>
        <div className="row gap-2">
          <Btn variant="ghost" size="sm" onClick={() => setMonth(new Date(year, mon - 1, 1))}>‹</Btn>
          <Btn
            variant="ghost"
            size="sm"
            onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)) }}
          >
            {lang === "id" ? "Hari ini" : "Today"}
          </Btn>
          <Btn variant="ghost" size="sm" onClick={() => setMonth(new Date(year, mon + 1, 1))}>›</Btn>
        </div>
      </div>
      {isMobile ? (
        <div className="col" style={{ padding: 8 }}>
          {monthSessions.length === 0 ? (
            <div className="muted small" style={{ textAlign: "center", padding: 24 }}>
              {lang === "id" ? "Tidak ada sesi bulan ini." : "No sessions this month."}
            </div>
          ) : (
            monthSessions.map((s) => {
              const d = new Date(s.scheduled_at)
              return (
                <div
                  key={s.id}
                  onClick={() => navigate("session-detail", { sessionId: s.id })}
                  className="row gap-3"
                  style={{ cursor: "pointer", padding: "10px 8px", borderBottom: "1px solid var(--border-subtle)" }}
                >
                  <div className="col" style={{ alignItems: "center", minWidth: 40 }}>
                    <div className="tiny muted">
                      {d.toLocaleDateString(lang === "id" ? "id-ID" : "en-US", { weekday: "short" })}
                    </div>
                    <div className="mono" style={{ fontSize: 18, fontWeight: 600 }}>{d.getDate()}</div>
                  </div>
                  <div className="col gap-1" style={{ flex: 1, minWidth: 0 }}>
                    <div className="text-truncate" style={{ fontWeight: 500 }}>{s.title}</div>
                    <div className="tiny muted text-truncate">
                      {d.toLocaleTimeString(lang === "id" ? "id-ID" : "en-US", { hour: "2-digit", minute: "2-digit" })}
                      {s.location ? ` · ${s.location}` : ""}
                    </div>
                  </div>
                  {s.is_active && (
                    <Badge tone="success" dot>{lang === "id" ? "Aktif" : "Live"}</Badge>
                  )}
                </div>
              )
            })
          )}
        </div>
      ) : (
        <div style={{ padding: 12, overflowX: isMobile ? "auto" : "visible" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              minWidth: isMobile ? 560 : undefined,
              gap: 1,
              background: "var(--border-subtle)",
              border: "1px solid var(--border-subtle)",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            {dayNames.map((dn) => (
              <div
                key={dn}
                className="tiny muted"
                style={{
                  background: "var(--surface-2)",
                  padding: "6px 8px",
                  textAlign: "center",
                  fontWeight: 600,
                }}
              >
                {dn}
              </div>
            ))}
            {days.map((d, i) => {
              const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
              const inMonth = d.getMonth() === mon
              const daySessions = byDay[key] || []
              return (
                <div
                  key={i}
                  style={{
                    background: key === todayKey ? "var(--program-color-soft)" : "var(--surface)",
                    minHeight: 92,
                    padding: 6,
                    opacity: inMonth ? 1 : 0.35,
                  }}
                >
                  <div
                    className="tiny mono"
                    style={{
                      fontWeight: key === todayKey ? 700 : 400,
                      color: key === todayKey ? "var(--program-color)" : "var(--text-3)",
                    }}
                  >
                    {d.getDate()}
                  </div>
                  <div className="col gap-1 mt-1">
                    {daySessions.map((s) => (
                      <div
                        key={s.id}
                        onClick={() => navigate("session-detail", { sessionId: s.id })}
                        title={s.title}
                        style={{
                          cursor: "pointer",
                          fontSize: 10,
                          padding: "2px 4px",
                          borderRadius: 3,
                          background: s.is_active ? "var(--program-color)" : "var(--surface-2)",
                          color: s.is_active ? "white" : "var(--text)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          border: "1px solid var(--border-subtle)",
                        }}
                      >
                        {new Date(s.scheduled_at).toLocaleTimeString(lang === "id" ? "id-ID" : "en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}{" "}
                        {s.title}
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function SessionFormModal({ programId, classId, session, onClose, onSaved }) {
  const { lang } = useApp()
  const isEdit = !!session
  const [title, setTitle] = React.useState(session?.title || "")
  const [scheduledAt, setScheduledAt] = React.useState(toLocalInputValue(session?.scheduled_at))
  const [location, setLocation] = React.useState(session?.location || "")
  const [locationLat, setLocationLat] = React.useState(session?.location_lat ?? "")
  const [locationLng, setLocationLng] = React.useState(session?.location_lng ?? "")
  const [geoRadiusM, setGeoRadiusM] = React.useState(session?.geo_radius_m ?? "")
  const [trainerId, setTrainerId] = React.useState(session?.trainer_id || "")
  const [sessionType, setSessionType] = React.useState(session?.session_type || "")
  // Yang diketik jam selesai; yang disimpan tetap menit — lihat lib/sessionTime.
  const [jamSelesai, setJamSelesai] = React.useState(
    jamSelesaiDari(toLocalInputValue(session?.scheduled_at).slice(11), session?.duration_min)
  )
  const [capacity, setCapacity] = React.useState(session?.capacity ?? "")
  const [conferenceMode, setConferenceMode] = React.useState(session?.conference_mode || "ONSITE")
  const [conferenceUrl, setConferenceUrl] = React.useState(session?.conference_url || "")
  const [zoomTerhubung, setZoomTerhubung] = React.useState(false)
  const [trainers, setTrainers] = React.useState([])
  const [recurrenceFreq, setRecurrenceFreq] = React.useState("")
  const [recurrenceCount, setRecurrenceCount] = React.useState(4)
  const [busy, setBusy] = React.useState(false)

  React.useEffect(() => {
    if (!programId) return
    listProgramTrainers(programId)
      .then(setTrainers)
      .catch(() => setTrainers([]))
  }, [programId])

  React.useEffect(() => {
    getZoomStatus()
      .then((r) => setZoomTerhubung(r.connected))
      .catch(() => setZoomTerhubung(false))
  }, [])

  const trainerOptions = React.useMemo(() => {
    const opts = trainers.map((tr) => ({
      id: tr.user_id,
      name: tr.user?.full_name || tr.user?.email || tr.user_id,
    }))
    if (session?.trainer && !opts.some((o) => o.id === session.trainer.id)) {
      opts.unshift({
        id: session.trainer.id,
        name:
          (session.trainer.full_name || session.trainer.email || "—") +
          (lang === "id" ? " (saat ini)" : " (current)"),
      })
    }
    return opts
  }, [trainers, session, lang])

  // Jam mulai diambil dari bagian waktu `scheduledAt` (datetime-local), jadi
  // jam selesai selalu dibandingkan terhadap hari yang sama.
  const durasi = durasiDariJam(scheduledAt.slice(11), jamSelesai)

  const submit = async (e) => {
    e.preventDefault()
    if (!durasi.valid) return
    setBusy(true)
    try {
      const payload = {
        title,
        scheduledAt: new Date(scheduledAt).toISOString(),
        location: location || null,
        locationLat: locationLat === "" ? null : Number(locationLat),
        locationLng: locationLng === "" ? null : Number(locationLng),
        geoRadiusM: geoRadiusM === "" ? null : Number(geoRadiusM),
        trainerId: trainerId || undefined,
        sessionType: sessionType || null,
        durationMin: durasi.menit,
        capacity: capacity === "" ? null : Number(capacity),
        conferenceMode,
        conferenceUrl: conferenceMode === "ONSITE" || !conferenceUrl ? null : conferenceUrl,
        conferenceProvider: conferenceMode !== "ONSITE" && conferenceUrl ? "ZOOM" : null,
      }
      if (!isEdit && recurrenceFreq && Number(recurrenceCount) > 1) {
        payload.recurrence = {
          frequency: recurrenceFreq,
          count: Number(recurrenceCount),
        }
      }
      const result = isEdit
        ? await updateSession(session.id, payload)
        // classId ikut supaya sesinya menempel ke angkatan yang sedang dibuka.
        // Tanpa itu sesinya tidak dimiliki kelas mana pun, dan kehadiran di
        // dalamnya tidak terhitung bagi peserta siapa pun.
        : await createSession({ ...payload, programId, ...(classId ? { classId } : {}) })
      const seriesN = result?.seriesCount || 1
      toast.success(
        isEdit
          ? (lang === "id" ? "Sesi diperbarui" : "Session updated")
          : seriesN > 1
            ? (lang === "id" ? `${seriesN} sesi dibuat` : `${seriesN} sessions created`)
            : (lang === "id" ? "Sesi dibuat" : "Session created")
      )
      for (const w of result?.warnings || []) {
        if (w.type === "zoom") {
          toast(
            (lang === "id" ? "Meeting Zoom gagal diproses: " : "Zoom meeting failed: ") + w.message,
            { icon: "⚠️", duration: 8000 }
          )
          continue
        }
        toast(
          (w.type === "trainer"
            ? (lang === "id" ? "Trainer bentrok dengan sesi: " : "Trainer conflicts with: ")
            : (lang === "id" ? "Lokasi bentrok dengan sesi: " : "Location conflicts with: ")
          ) + w.sessionTitle,
          { icon: "⚠️", duration: 6000 }
        )
      }
      onSaved()
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setBusy(false)
    }
  }

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error(lang === "id" ? "Geolokasi tidak didukung browser" : "Geolocation not supported")
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocationLat(String(pos.coords.latitude))
        setLocationLng(String(pos.coords.longitude))
        toast.success(lang === "id" ? "Lokasi terisi" : "Location filled")
      },
      () => toast.error(lang === "id" ? "Gagal mendapatkan lokasi" : "Could not get location")
    )
  }

  return (
    <div
      className="modal-overlay"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", display: "grid", placeItems: "center", zIndex: 50 }}
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="card"
        style={{ width: 520, background: "var(--surface)", maxHeight: "90vh", overflow: "auto" }}
      >
        <div className="card-header">
          <div className="h3">
            {isEdit
              ? (lang === "id" ? "Ubah Sesi" : "Edit Session")
              : (lang === "id" ? "Buat Sesi Baru" : "New Session")}
          </div>
          <Btn type="button" variant="ghost" size="sm" onClick={onClose}>
            <Icon name="close" size={14} />
          </Btn>
        </div>

        <div className="col gap-3" style={{ padding: "16px 20px" }}>
          {/* Sesi tanpa kelas tidak dimiliki angkatan mana pun, sehingga
              kehadiran di dalamnya tidak terhitung bagi siapa pun. Diperingatkan
              di sini karena akibatnya baru ketahuan berminggu-minggu kemudian,
              saat sertifikat seseorang ternyata tidak terbit. */}
          {!isEdit && !classId && (
            <div
              className="small"
              style={{
                padding: "10px 12px", borderRadius: 8, lineHeight: 1.55,
                background: "var(--surface-2)", border: "1px solid var(--border-subtle)",
              }}
            >
              {lang === "id"
                ? "Belum ada kelas yang dipilih. Sesi ini tidak akan menempel pada angkatan mana pun, jadi kehadirannya tidak dihitung untuk peserta siapa pun. Pilih kelas di bilah sisi lebih dulu."
                : "No class selected. This session will not belong to any class, so attendance in it counts for nobody. Pick a class in the sidebar first."}
            </div>
          )}
          <label className="col gap-1">
            <span className="small">{lang === "id" ? "Judul" : "Title"}</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder={lang === "id" ? "Drill Pemasangan Boom" : "Boom Deployment Drill"}
              className="input"
            />
          </label>

          <label className="col gap-1">
            <span className="small">{lang === "id" ? "Jadwal" : "Schedule"}</span>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              required
              className="input"
            />
          </label>

          <div className="row gap-3">
            <label className="col gap-1" style={{ flex: 1 }}>
              <span className="small">{lang === "id" ? "Tipe sesi (opsional)" : "Session type (optional)"}</span>
              <select
                value={sessionType}
                onChange={(e) => setSessionType(e.target.value)}
                className="input"
              >
                <option value="">{lang === "id" ? "— pilih tipe —" : "— select type —"}</option>
                <option value="LECTURE">{lang === "id" ? "Kuliah / Materi" : "Lecture"}</option>
                <option value="DRILL">{lang === "id" ? "Drill Praktik" : "Practical Drill"}</option>
                <option value="EXAM">{lang === "id" ? "Ujian" : "Exam"}</option>
              </select>
            </label>
            <label className="col gap-1" style={{ width: 160 }}>
              <span className="small">
                {lang === "id" ? "Jam selesai (opsional)" : "End time (optional)"}
              </span>
              <input
                type="time"
                value={jamSelesai}
                onChange={(e) => setJamSelesai(e.target.value)}
                className="input"
              />
              <span className={durasi.valid ? "tiny muted" : "tiny"} style={durasi.valid ? undefined : { color: "var(--danger)" }}>
                {durasi.kosong
                  ? (lang === "id" ? "Kosongkan bila belum pasti." : "Leave blank if not fixed yet.")
                  : durasi.valid
                    ? labelDurasi(durasi.menit, lang === "id")
                    : (lang === "id"
                      ? "Harus setelah jam mulai, di hari yang sama."
                      : "Must be after the start time, same day.")}
              </span>
            </label>
          </div>

          <label className="col gap-1">
            <span className="small">{lang === "id" ? "Lokasi (opsional)" : "Location (optional)"}</span>
            <input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder={lang === "id" ? "Pelabuhan Tanjung Priok" : "Tanjung Priok Port"}
              className="input"
            />
          </label>

          <label className="col gap-1">
            <span className="small">Trainer</span>
            <select
              value={trainerId}
              onChange={(e) => setTrainerId(e.target.value)}
              className="input"
            >
              <option value="">
                {lang === "id" ? "— default (pembuat sesi) —" : "— default (creator) —"}
              </option>
              {trainerOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </label>

          <label className="col gap-1">
            <span className="small">{lang === "id" ? "Kapasitas (opsional)" : "Capacity (optional)"}</span>
            <input
              type="number"
              min="1"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              placeholder={lang === "id" ? "Maks. peserta RSVP" : "Max RSVP participants"}
              className="input"
            />
          </label>

          <div className="row gap-3">
            <label className="col gap-1" style={{ width: 160 }}>
              <span className="small">{lang === "id" ? "Mode" : "Mode"}</span>
              <select value={conferenceMode} onChange={(e) => setConferenceMode(e.target.value)} className="input">
                <option value="ONSITE">{lang === "id" ? "Tatap muka" : "On-site"}</option>
                <option value="HYBRID">Hybrid</option>
                <option value="REMOTE">{lang === "id" ? "Daring (Zoom)" : "Remote (Zoom)"}</option>
              </select>
            </label>
            {conferenceMode !== "ONSITE" && (
              <label className="col gap-1" style={{ flex: 1, minWidth: 0 }}>
                <span className="small">{lang === "id" ? "URL Zoom / meeting" : "Zoom / meeting URL"}</span>
                <input
                  type="url"
                  value={conferenceUrl}
                  onChange={(e) => setConferenceUrl(e.target.value)}
                  placeholder={zoomTerhubung
                    ? (lang === "id" ? "Kosongkan untuk dibuat otomatis" : "Leave blank to create automatically")
                    : "https://zoom.us/j/..."}
                  className="input"
                  readOnly={Boolean(session?.zoom_meeting_id)}
                />
                {session?.zoom_meeting_id && (
                  <span className="tiny muted">
                    {lang === "id"
                      ? "Dibuat otomatis. Jadwal dan judul mengikuti perubahan sesi ini."
                      : "Created automatically. The schedule and title follow changes to this session."}
                  </span>
                )}
              </label>
            )}
          </div>

          <div className="col gap-1">
            <span className="small">
              {lang === "id" ? "Geo-fence absensi (opsional)" : "Attendance geo-fence (optional)"}
            </span>
            <div className="row gap-2">
              <input
                type="number"
                step="any"
                value={locationLat}
                onChange={(e) => setLocationLat(e.target.value)}
                placeholder="Lat"
                className="input"
                style={{ flex: 1, minWidth: 0 }}
              />
              <input
                type="number"
                step="any"
                value={locationLng}
                onChange={(e) => setLocationLng(e.target.value)}
                placeholder="Lng"
                className="input"
                style={{ flex: 1, minWidth: 0 }}
              />
              <input
                type="number"
                value={geoRadiusM}
                onChange={(e) => setGeoRadiusM(e.target.value)}
                placeholder={lang === "id" ? "Radius (m)" : "Radius (m)"}
                className="input"
                style={{ width: 100 }}
              />
              <Btn type="button" variant="ghost" size="sm" onClick={useMyLocation}>
                {lang === "id" ? "Lokasi saya" : "My location"}
              </Btn>
            </div>
            <span className="tiny muted">
              {lang === "id"
                ? "Isi lat, lng & radius untuk menandai absensi yang discan di luar lokasi."
                : "Fill lat, lng & radius to flag attendance scanned outside the venue."}
            </span>
          </div>

          {!isEdit && (
            <div className="row gap-3">
              <label className="col gap-1" style={{ flex: 1 }}>
                <span className="small">{lang === "id" ? "Ulangi" : "Repeat"}</span>
                <select
                  value={recurrenceFreq}
                  onChange={(e) => setRecurrenceFreq(e.target.value)}
                  className="input"
                >
                  <option value="">{lang === "id" ? "Tidak berulang" : "Does not repeat"}</option>
                  <option value="WEEKLY">{lang === "id" ? "Mingguan" : "Weekly"}</option>
                  <option value="DAILY">{lang === "id" ? "Harian" : "Daily"}</option>
                </select>
              </label>
              {recurrenceFreq && (
                <label className="col gap-1" style={{ width: 140 }}>
                  <span className="small">{lang === "id" ? "Jumlah sesi" : "Occurrences"}</span>
                  <input
                    type="number"
                    min="2"
                    max="52"
                    value={recurrenceCount}
                    onChange={(e) => setRecurrenceCount(e.target.value)}
                    className="input"
                  />
                </label>
              )}
            </div>
          )}
        </div>

        <div className="card-footer" style={{ padding: "12px 20px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Btn type="button" variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            {lang === "id" ? "Batal" : "Cancel"}
          </Btn>
          <Btn type="submit" variant="primary" size="sm" disabled={busy || !title || !scheduledAt || !durasi.valid}>
            {busy
              ? (lang === "id" ? "Menyimpan..." : "Saving...")
              : (lang === "id" ? "Simpan" : "Save")}
          </Btn>
        </div>
      </form>
    </div>
  )
}
